/**
 * Client-side error capture for reporting to backend ingest endpoint.
 * Captures window.onerror, window.onunhandledrejection, and provides reportErrorToBackend.
 * @see .Cursor/plans/error_logging_&_error_ticket_workflow
 */

const ALLOWED_KEYS = [
  'name',
  'message',
  'stack',
  'route',
  'method',
  'statusCode',
  'requestId',
  'buildVersion',
  'userAgent',
  'metadata',
];

const JWT_PATTERN = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;

/**
 * Redact stack string if it contains URLs with query params (potential tokens).
 */
function redactStack(stack) {
  if (!stack || typeof stack !== 'string') return stack;
  return stack.replace(JWT_PATTERN, '[REDACTED_JWT]');
}

/**
 * Pick only allowlisted keys and redact sensitive data before sending.
 */
function preparePayload(payload) {
  const out = {};
  for (const key of ALLOWED_KEYS) {
    if (Object.prototype.hasOwnProperty.call(payload, key) && payload[key] != null) {
      let value = payload[key];
      if (key === 'stack') {
        value = redactStack(value);
      } else if (typeof value === 'string' && key !== 'message') {
        value = String(value).replace(JWT_PATTERN, '[REDACTED_JWT]');
      }
      out[key] = value;
    }
  }
  return out;
}

/**
 * Get the API base URL for fetch (same logic as api/index.js).
 */
function getBaseURL() {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  if (import.meta.env.DEV) {
    return '/api';
  }
  return '/api';
}

/**
 * Report an error to the backend ingest endpoint. Fire-and-forget, non-blocking.
 * @param {object} payload - { message, name?, stack?, route?, method?, statusCode?, requestId?, buildVersion?, userAgent?, metadata? }
 */
export function reportErrorToBackend(payload) {
  const safe = preparePayload(payload);
  if (!safe.message && !safe.name) {
    safe.message = 'Unknown error';
  }
  const base = getBaseURL();
  const url = base.endsWith('/') ? `${base}errors/ingest` : `${base}/errors/ingest`;
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(safe),
    keepalive: true,
  }).catch(() => {
    // Silently ignore network failures when reporting errors
  });
}

/**
 * Initialize global error handlers: window.onerror and window.onunhandledrejection.
 */
export function initErrorCapture() {
  const prevOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    const payload = {
      message: String(message),
      name: error?.name,
      stack: error?.stack,
      route: window.location.pathname || undefined,
      method: undefined,
      statusCode: undefined,
      requestId: undefined,
      buildVersion: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : undefined,
      userAgent: navigator?.userAgent || undefined,
      metadata: source ? { source, lineno, colno } : undefined,
    };
    reportErrorToBackend(payload);
    if (typeof prevOnError === 'function') {
      return prevOnError(message, source, lineno, colno, error);
    }
    return false;
  };

  const prevOnUnhandledRejection = window.onunhandledrejection;
  window.onunhandledrejection = (event) => {
    const reason = event.reason;
    const message = reason?.message ?? (typeof reason === 'string' ? reason : 'Unhandled rejection');
    const stack = reason?.stack;
    const payload = {
      message: String(message),
      name: reason?.name,
      stack,
      route: window.location.pathname || undefined,
      buildVersion: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : undefined,
      userAgent: navigator?.userAgent || undefined,
    };
    reportErrorToBackend(payload);
    if (typeof prevOnUnhandledRejection === 'function') {
      prevOnUnhandledRejection(event);
    }
  };
}
